import { useEffect, useRef, useState, useCallback } from 'react';
import { createAgents, updatePhysics } from '../game/core';
import { GAME_CONFIG } from '../game/constants';

export const useGameLoop = (canvasRef) => {
  const agentsRef = useRef([]);
  const requestRef = useRef();
  const canvasSizeRef = useRef({ w: 0, h: 0, radius: 0 });
  const statsRef = useRef([0, 0, 0]);
  
  const [stats, setStats] = useState([0, 0, 0]);
  const [totalRounds, setTotalRounds] = useState(0);
  const [winCounts, setWinCounts] = useState([0, 0, 0]);
  const [isRunning, setIsRunning] = useState(false);
  const [winner, setWinner] = useState(null);

  const resetGame = useCallback(() => {
    const { w, h, radius } = canvasSizeRef.current;
    if (w === 0 || h === 0) return;
    
    agentsRef.current = createAgents(w, h, radius);
    setWinner(null);
    setIsRunning(true);
    
    const initialCount = Math.floor(GAME_CONFIG.AGENTS_COUNT / 3);
    const initialStats = [initialCount, initialCount, initialCount];
    setStats(initialStats);
    statsRef.current = initialStats;
  }, []);

  const animate = useCallback(() => {
    if (!isRunning) return;

    const { w, h, radius } = canvasSizeRef.current;
    if (w === 0) {
      requestRef.current = requestAnimationFrame(animate);
      return;
    }

    const { winnerType, counts } = updatePhysics(agentsRef.current, w, h, radius);
    statsRef.current = counts;

    if (winnerType !== null) {
      // 修復核心：在停止遊戲前，確保 UI 狀態與最終物理統計完全一致
      setStats([...counts]); 
      
      setIsRunning(false);
      setWinner(winnerType);
      setTotalRounds(prev => prev + 1);
      setWinCounts(prev => {
        const next = [...prev];
        next[winnerType]++;
        return next;
      });

      setTimeout(() => resetGame(), GAME_CONFIG.VICTORY_DURATION);
    } else {
      requestRef.current = requestAnimationFrame(animate);
    }
  }, [isRunning, resetGame]);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const w = canvas.parentElement.clientWidth;
      const h = canvas.parentElement.clientHeight;
      canvas.width = w;
      canvas.height = h;
      const radius = Math.round(Math.max(GAME_CONFIG.MIN_RADIUS, Math.min(w, h) / 22));
      canvasSizeRef.current = { w, h, radius };
      if (agentsRef.current.length === 0) resetGame();
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [canvasRef, resetGame]);

  useEffect(() => {
    if (isRunning) requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [isRunning, animate]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isRunning) setStats([...statsRef.current]);
    }, 80); // 稍微提高同步頻率
    return () => clearInterval(interval);
  }, [isRunning]);

  return {
    agents: agentsRef.current,
    stats,
    totalRounds,
    winCounts,
    isRunning,
    setIsRunning,
    winner,
    fullReset: () => { setTotalRounds(0); setWinCounts([0,0,0]); resetGame(); }
  };
};